import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, formatAxisCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Plus, CheckCircle2, Trash2, TrendingUp, TrendingDown, Landmark, HandCoins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const TYPE_LABELS = {
  expense: { label: "Despesa", cls: "bg-red-500/20 text-red-600" },
  income: { label: "Receita", cls: "bg-emerald-500/20 text-emerald-600" },
  payable: { label: "A Pagar", cls: "bg-amber-500/20 text-amber-600" },
  receivable: { label: "A Receber", cls: "bg-blue-500/20 text-blue-600" },
};

const emptyForm = { type: "expense", description: "", category: "geral", amount: "", due_date: "", status: "pending" };

export default function FinancePage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, [filterType, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("type", filterType);
      if (filterStatus !== "all") params.append("status", filterStatus);
      const [sRes, tRes] = await Promise.all([
        axios.get(`${API_URL}/finance/summary`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/finance/transactions?${params}`, { headers, withCredentials: true }),
      ]);
      setSummary(sRes.data);
      setTransactions(tRes.data);
    } catch { toast.error("Erro ao carregar dados financeiros"); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/finance/transactions`, {
        ...form,
        amount: toBaseCurrency(form.amount),
        due_date: form.due_date || null,
      }, { headers, withCredentials: true });
      toast.success("Transação registada");
      setModal(false); setForm(emptyForm);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao registar"); }
  };

  const markPaid = async (tx) => {
    try {
      await axios.put(`${API_URL}/finance/transactions/${tx.transaction_id}/pay`, {}, { headers, withCredentials: true });
      toast.success("Marcada como paga");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (tx) => {
    if (!window.confirm("Remover esta transação?")) return;
    try {
      await axios.delete(`${API_URL}/finance/transactions/${tx.transaction_id}`, { headers, withCredentials: true });
      toast.success("Removida");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const cm = summary?.current_month;

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Financeiro</h1>
          <p className="text-muted-foreground">Receitas, despesas, contas a pagar e a receber</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setModal(true); }} data-testid="add-transaction-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova Transação
        </Button>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-emerald-600"><TrendingUp className="w-4 h-4" /><p className="text-xs">Receitas (mês)</p></div>
          <p className="text-lg font-bold font-mono" data-testid="finance-income">{formatCurrency(cm?.income)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-red-600"><TrendingDown className="w-4 h-4" /><p className="text-xs">Despesas (mês)</p></div>
          <p className="text-lg font-bold font-mono">{formatCurrency(cm?.expenses)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2"><Wallet className="w-4 h-4" /><p className="text-xs">Resultado (mês)</p></div>
          <p className={`text-lg font-bold font-mono ${(cm?.net || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatCurrency(cm?.net)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-amber-600"><Landmark className="w-4 h-4" /><p className="text-xs">Contas a Pagar</p></div>
          <p className="text-lg font-bold font-mono">{formatCurrency(summary?.pending_payables)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-blue-600"><HandCoins className="w-4 h-4" /><p className="text-xs">Contas a Receber</p></div>
          <p className="text-lg font-bold font-mono">{formatCurrency(summary?.pending_receivables)}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Fluxo de Caixa (6 meses)</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => formatAxisCurrency(v)} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Legend />
                <Bar dataKey="income" name="Receitas" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Despesas" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44" data-testid="filter-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="expense">Despesas</SelectItem>
            <SelectItem value="income">Receitas</SelectItem>
            <SelectItem value="payable">Contas a Pagar</SelectItem>
            <SelectItem value="receivable">Contas a Receber</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="paid">Pagas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 divide-y max-h-[500px] overflow-y-auto">
          {loading ? <p className="p-6 text-muted-foreground">A carregar...</p> :
            transactions.length === 0 ? <p className="p-6 text-center text-muted-foreground">Sem transações</p> :
            transactions.map((tx) => (
              <div key={tx.transaction_id} className="flex items-center justify-between p-3 gap-3" data-testid={`transaction-${tx.transaction_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={TYPE_LABELS[tx.type]?.cls}>{TYPE_LABELS[tx.type]?.label}</Badge>
                    <p className="font-medium text-sm">{tx.description}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {tx.category} · {formatDate(tx.created_at)}{tx.due_date ? ` · vence: ${tx.due_date}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-mono font-bold">{formatCurrency(tx.amount)}</p>
                  <Badge variant="outline" className={tx.status === "paid" ? "text-emerald-600" : "text-amber-600"}>
                    {tx.status === "paid" ? "Paga" : "Pendente"}
                  </Badge>
                  {tx.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(tx)} data-testid={`pay-tx-${tx.transaction_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Pagar
                    </Button>
                  )}
                  {tx.source === "manual" && (
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(tx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova Transação</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Despesa</SelectItem>
                    <SelectItem value="income">Receita</SelectItem>
                    <SelectItem value="payable">Conta a Pagar</SelectItem>
                    <SelectItem value="receivable">Conta a Receber</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral">Geral</SelectItem>
                    <SelectItem value="compras">Compras</SelectItem>
                    <SelectItem value="salarios">Salários</SelectItem>
                    <SelectItem value="renda">Renda</SelectItem>
                    <SelectItem value="energia">Energia/Água</SelectItem>
                    <SelectItem value="manutencao">Manutenção</SelectItem>
                    <SelectItem value="impostos">Impostos</SelectItem>
                    <SelectItem value="outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Descrição *</Label>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tx-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Valor *</Label>
                <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-amount-input" />
              </div>
              <div className="space-y-1">
                <Label>Data de Vencimento</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="paid">Já paga</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-tx-btn">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
